import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Crown, User, Mail, Key } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { useAuth } from '../../context/AuthContext';
import { PRICING_TIERS } from '../../lib/stripe';
import { getGravatarUrl } from '../../lib/gravatar';
import { supabase } from '../../lib/supabase';

export default function AccountPanel() {
  const { subscription } = useSubscription();
  const { user } = useAuth();
  const [purchaseDate, setPurchaseDate] = React.useState<string | null>(null);
  const tierLimits = PRICING_TIERS[subscription?.tier || 'FREE'];
  const gravatarUrl = user?.email ? getGravatarUrl(user.email, 100) : null;

  React.useEffect(() => {
    async function fetchPurchaseDate() {
      if (!user?.id) return;

      try {
        // First try user_profiles
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('purchase_date')
          .eq('user_id', user.id)
          .single();

        if (profileError) throw profileError;

        if (profileData?.purchase_date) {
          setPurchaseDate(profileData.purchase_date);
          return;
        }

        // Fallback to subscriptions table
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('subscriptions')
          .select('created_at')
          .eq('user_id', user.id)
          .eq('plan_id', 'lifetime')
          .single();

        if (subscriptionError) throw subscriptionError;

        if (subscriptionData?.created_at) {
          setPurchaseDate(subscriptionData.created_at);
        }
      } catch (error) {
        console.error('Error fetching purchase date:', error);
      }
    }

    if (subscription?.tier === 'LIFETIME') {
      fetchPurchaseDate();
    }
  }, [user, subscription]);

  return (
    <div className="mt-6 border-t border-gray-200 pt-6">
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-start space-x-6">
            {gravatarUrl && (
              <img
                src={gravatarUrl}
                alt="Profile"
                className="h-24 w-24 rounded-full border-2 border-gray-200"
              />
            )}
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Account Information
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <User className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="font-medium mr-2">User ID:</span>
                  <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                    {user?.id}
                  </span>
                </div>
                
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="font-medium mr-2">Email:</span>
                  <span>{user?.email}</span>
                </div>

                <div className="flex items-center text-sm text-gray-600">
                  <Key className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="font-medium mr-2">Account Type:</span>
                  <span className="flex items-center">
                    {subscription?.isAdmin ? (
                      <span className="flex items-center text-purple-700">
                        <Crown className="h-4 w-4 mr-1" />
                        Administrator
                      </span>
                    ) : (
                      tierLimits.name
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 border-t border-gray-200 pt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-4">
              Subscription Status
            </h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <p className="text-sm font-medium text-gray-900">Current Plan:</p>
                  <p className="ml-2 text-sm font-semibold text-gray-900 flex items-center">
                    {tierLimits.name}
                    {subscription?.isAdmin && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        <Crown className="h-4 w-4 mr-1" />
                        Admin
                      </span>
                    )}
                  </p>
                </div>
                {subscription?.tier === 'FREE' && !subscription?.isAdmin && (
                  <Link
                    to="/pricing"
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
                  >
                    Upgrade Plan
                  </Link>
                )}
              </div>

              <div className="mt-4 space-y-2">
                {subscription?.isAdmin ? (
                  <div className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    Unlimited access to all features (Admin)
                  </div>
                ) : (
                  tierLimits.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      {feature}
                    </div>
                  ))
                )}
              </div>

              {subscription?.tier === 'LIFETIME' && purchaseDate && (
                <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-3">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    <p className="text-sm text-green-700">
                      Lifetime access activated on{' '}
                      {new Date(purchaseDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}